import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Toast from '../components/Toast';
import GymnastRoutineCard from '../components/routines/GymnastRoutineCard';
import GymnastRoutineEditor from '../components/routines/GymnastRoutineEditor';
import SendBackDialog from '../components/routines/SendBackDialog';
import { APPARATUS } from '../components/routines/routineConstants';
import { getConnectionStatus, subscribeConnection } from '../utils/connectionStatus';
import '../components/routines/GymnastRoutines.css';

const errorMessage = (err, fallback) => err.response?.data?.error || fallback;
const apparatusLabel = value => APPARATUS.find(a => a.value === value)?.label || value;
const dayLabel = iso => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

// Admin, Skill Tracking: routines gymnasts entered that are waiting for a
// coach. Any coach can approve, send back with a note, fix and approve, or
// delete.
export default function RoutinesToCheck() {
  const [error, setError] = useState(null);
  const [routines, setRoutines] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [sendingBack, setSendingBack] = useState(null);
  const [online, setOnline] = useState(() => getConnectionStatus() !== 'offline');
  const [toast, setToast] = useState(null);
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    axios.get('/api/gymnast-routines/to-check')
      .then(res => setRoutines(res.data))
      .catch(err => setError(errorMessage(err, 'Could not load the routines to check.')));
  }, []);
  useEffect(() => subscribeConnection(status => setOnline(status !== 'offline')), []);

  const done = (routine, message) => {
    setRoutines(list => list.filter(r => r.id !== routine.id));
    setOpenId(null);
    setToast({ message });
  };
  const failed = (err, fallback) => setToast({ message: errorMessage(err, fallback), type: 'error' });

  const approve = async (routine) => {
    try {
      await axios.post(`/api/gymnast-routines/${routine.id}/verify`);
      done(routine, `${routine.name} approved`);
    } catch (err) { failed(err, 'Failed to approve routine'); }
  };
  const sendBack = async (note) => {
    const routine = sendingBack;
    try {
      await axios.post(`/api/gymnast-routines/${routine.id}/send-back`, { note });
      setSendingBack(null);
      done(routine, `${routine.name} sent back to ${routine.gymnast.firstName}`);
    } catch (err) { failed(err, 'Failed to send back routine'); throw err; }
  };
  const saveAndApprove = async (payload) => {
    const routine = editing;
    try {
      await axios.put(`/api/gymnast-routines/${routine.id}`, { ...payload, approve: true });
      setEditing(null);
      done(routine, `${payload.name || routine.name} saved and approved`);
    } catch (err) { failed(err, 'Failed to save routine'); throw err; }
  };
  const remove = async (routine) => {
    if (!window.confirm(`Delete ${routine.gymnast.firstName}'s routine "${routine.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`/api/gymnast-routines/${routine.id}`);
      done(routine, 'Routine deleted');
    } catch (err) { failed(err, 'Failed to delete routine'); }
  };

  return (
    <div className="bk-page routines-to-check">
      <h2>Routines to check</h2>
      <p className="text-muted">Routines gymnasts have added or changed, oldest first.</p>
      {error && <p className="bk-error">{error}</p>}
      {!routines && !error && <p className="text-muted">Loading…</p>}
      {routines?.length === 0 && <p>Nothing to check.</p>}
      {routines?.length > 0 && !online && <p className="text-muted routines-to-check__offline">Checking routines needs signal.</p>}
      {routines?.map(routine => {
        const open = openId === routine.id;
        return (
          <div key={routine.id} className="routines-to-check__item">
            <button
              type="button"
              className="routines-to-check__row"
              aria-expanded={open}
              onClick={() => setOpenId(open ? null : routine.id)}
            >
              <span className="routines-to-check__who">{routine.gymnast.firstName} {routine.gymnast.lastName}</span>
              <span className="routines-to-check__what">
                {routine.name} · {apparatusLabel(routine.apparatus)}
                {routine.totalDifficulty > 0 && ` · DD ${routine.totalDifficulty.toFixed(1)}`}
              </span>
              <span className="routines-to-check__when">{dayLabel(routine.updatedAt)}</span>
            </button>
            {open && (
              <div className="routines-to-check__detail">
                <GymnastRoutineCard routine={routine} canEdit={false} canSubmit={false} />
                <div className="routines-to-check__actions">
                  <button type="button" className="btn btn-sm btn-success" disabled={!online} onClick={() => approve(routine)}>Approve</button>
                  <button type="button" className="btn btn-sm btn-outline" disabled={!online} onClick={() => setSendingBack(routine)}>Send back</button>
                  <button type="button" className="btn btn-sm btn-outline" disabled={!online} onClick={() => setEditing(routine)}>Edit and approve</button>
                  <button type="button" className="btn btn-sm btn-outline routines-to-check__delete" disabled={!online} onClick={() => remove(routine)}>Delete</button>
                  <Link className="routines-to-check__open" to={`/progress/${routine.gymnast.id}?tab=routines`}>Open {routine.gymnast.firstName}'s routines</Link>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {editing && (
        <GymnastRoutineEditor
          routine={editing}
          gymnastId={editing.gymnast.id}
          saveLabel="Save and approve"
          onSave={saveAndApprove}
          onCancel={() => setEditing(null)}
        />
      )}
      {sendingBack && <SendBackDialog routine={sendingBack} onSend={sendBack} onCancel={() => setSendingBack(null)} />}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />}
    </div>
  );
}
